// ---------------------------------------------------------------------------
// register — helper to register the Cogo KG Service Worker from an app's
// entry point (main.tsx).
//
// Usage (per-app):
//   import { registerKgSw } from '@cogo/kg-sw'
//   if (import.meta.env.VITE_KG_BROWSER) registerKgSw()
//
// The SW script itself is shipped as `/sw.js` at the app's public root. The
// app's build pipeline copies `packages/cogo-kg-sw/dist/sw.js` to its
// `public/sw.js` via a postinstall or prebuild hook.
//
// References:
//   - docs/agentic/architecture/wasm-client-distribution.md §8
//   - .planning/phase-2a-step-6-results.md §6 (Step 7 design sketch)
// ---------------------------------------------------------------------------

export interface RegisterKgSwOptions {
  /**
   * URL to the SW script. Defaults to `/sw.js` (same-origin root scope).
   * Cross-origin SW registration is NOT supported by browsers — this must
   * be a same-origin URL.
   */
  scriptUrl?: string

  /**
   * SW scope. Defaults to `/` (whole app). Pages fetched outside the scope
   * are not controlled by this SW.
   */
  scope?: string

  /**
   * Register strategy:
   *  - 'eager': register immediately (default; fine for most apps)
   *  - 'on-load': defer until window 'load' event to avoid competing with
   *    initial LCP resources
   */
  strategy?: 'eager' | 'on-load'

  /** If true, emit debug logs via console.debug. Default false. */
  verbose?: boolean

  /**
   * Called once the SW transitions to 'activated' state OR immediately if
   * already controlling the page. Useful for apps that want to gate the
   * first kg-browser fetch on SW readiness.
   */
  onActive?: (registration: ServiceWorkerRegistration) => void

  /** Called on registration failure (CSP block, invalid SW, etc.). */
  onError?: (err: unknown) => void
}

export interface RegisterKgSwResult {
  /** The created registration (null on failure or when SW API unavailable). */
  registration: ServiceWorkerRegistration | null
  /** Reason for skip, if `registration` is null. */
  skipped?: 'no-sw-support' | 'ssr' | 'registration-failed'
}

/**
 * Register the Cogo KG Service Worker. Safe to call on any page — no-ops if:
 *   - `navigator.serviceWorker` is undefined (old browser / insecure context)
 *   - Running during SSR (no `window`)
 *
 * Idempotent: calling twice returns the same registration.
 */
export async function registerKgSw(
  opts: RegisterKgSwOptions = {},
): Promise<RegisterKgSwResult> {
  if (typeof window === 'undefined') {
    return { registration: null, skipped: 'ssr' }
  }
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return { registration: null, skipped: 'no-sw-support' }
  }

  const scriptUrl = opts.scriptUrl ?? '/sw.js'
  const scope = opts.scope ?? '/'
  const strategy = opts.strategy ?? 'eager'
  const verbose = opts.verbose ?? false

  const doRegister = async (): Promise<RegisterKgSwResult> => {
    try {
      const registration = await navigator.serviceWorker.register(scriptUrl, {
        scope,
        type: 'classic',
      })
      if (verbose) {
        console.debug('[cogo-kg-sw] registered', {
          scope: registration.scope,
          active: !!registration.active,
          installing: !!registration.installing,
          waiting: !!registration.waiting,
        })
      }

      // Resolve onActive when SW is ready to control the page.
      if (opts.onActive) {
        const ready = await navigator.serviceWorker.ready
        opts.onActive(ready)
      }

      return { registration }
    } catch (err) {
      if (verbose) console.debug('[cogo-kg-sw] registration failed:', err)
      opts.onError?.(err)
      return { registration: null, skipped: 'registration-failed' }
    }
  }

  if (strategy === 'on-load' && document.readyState !== 'complete') {
    return new Promise<RegisterKgSwResult>((resolve) => {
      window.addEventListener(
        'load',
        () => {
          doRegister().then(resolve).catch(() => resolve({ registration: null, skipped: 'registration-failed' }))
        },
        { once: true },
      )
    })
  }

  return doRegister()
}

/**
 * Unregister all Cogo KG SWs for the current origin. Useful for:
 *   - Feature-flag rollback (flip VITE_KG_BROWSER off + redeploy)
 *   - Tests
 */
export async function unregisterKgSw(): Promise<number> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return 0
  const registrations = await navigator.serviceWorker.getRegistrations()
  let count = 0
  for (const reg of registrations) {
    if (reg.active?.scriptURL?.endsWith('/sw.js')) {
      const ok = await reg.unregister()
      if (ok) count++
    }
  }
  return count
}

/**
 * Tell the waiting SW (if any) to skip waiting and activate immediately.
 * Useful for apps that want to apply SW updates without a hard reload.
 */
export function skipWaitingKgSw(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  navigator.serviceWorker.getRegistration().then((reg) => {
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  })
}

/**
 * Broadcast an invalidation message to all tabs + the SW. Used by
 * kg-browser's manifest-diff loop when a `shapeSha` change is detected.
 *
 * @returns `true` if BroadcastChannel is available, `false` otherwise.
 */
export function broadcastKgInvalidate(
  message:
    | { type: 'shapeSha-changed'; sha?: string }
    | { type: 'shard-invalidated'; url?: string }
    | { type: 'manifest-invalidated' },
): boolean {
  if (typeof BroadcastChannel === 'undefined') return false
  try {
    const channel = new BroadcastChannel('cogo-kg-invalidate')
    channel.postMessage(message)
    channel.close()
    return true
  } catch {
    return false
  }
}
